import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { customerColumn } from "Constants/TableColumns";
import { Grid } from "@material-ui/core";
import RemoteTable from "../../../../Components/RemoteTable/RemoteTable";
import Translator from "Languages/Utility/Translator";
import { getAllMoshtariTblData, changeCustomerSearch } from "Stores/actions";
import { read } from "Helpers/LocalStorage";
import Search from "Assets/svg/icon/search.svg";
import Input from "Components/Input/Input";
import { ReactSVG } from "react-svg";
import BackDropLoading from "Components/BackDropLoading/BackDropLoading";
import { useTranslation } from "react-i18next";
import { convertToEnglishNumber } from "Helpers/convertToEnglishNumber";
import { useSetState } from "Helpers/Hooks";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";

function CustomerTable() {
  const { t } = useTranslation();
  const [controls, setControls] = useSetState({
    customersType: {
      type: "select",
      selectItems: [
        { label: t("cusall"), value: 0 },
        { label: t("cusreg"), value: 1 },
        { label: t("cusshop"), value: 2 },
      ],
      value: { label: t("cusall"), value: 0 },
      validation: null,
    },
  });
  const { customerData, customerDataPagination, customerDataSearch } =
    useSelector((state) => ({
      customerData: state.customer.customerData,
      customerDataSearch: state.customer.customerDataSearch,
      customerDataPagination: state.customer.customerDataPagination,
    }));
  const dispatch = useDispatch();

  const [sizePerPage, setSizePerPage] = useState(customerDataPagination.Limit);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(customerDataPagination.Page);
  const [total, setTotal] = useState(1);
  const [tblData, setTblData] = useState(customerData);
  const firstUpdate = useRef(true);

  useEffect(() => {
    if (customerData) {
      // console.log(customerData);
      setTblData(customerData);
      setTotal(customerDataPagination.Total);
    }
  }, [customerData, customerDataPagination]);

  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (customerDataSearch) {
      const timer = setTimeout(() => {
        try {
          setLoading(true);
          const params = {
            KeyGroup: read("groupKey"),
            KeyUser: read("userKey"),
            TypeRegister: controls.customersType.value.value,
            Limit: sizePerPage,
            Page: 1,
            Name: customerDataSearch.Name,
          };
          dispatch(getAllMoshtariTblData(params));
          setCurrentPage(1);
          setLoading(false);
        } catch (e) {
          setLoading(false);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [customerDataSearch]);

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    try {
      setLoading(true);
      const params = {
        KeyGroup: read("groupKey"),
        KeyUser: read("userKey"),
        TypeRegister: value.value,
        Limit: sizePerPage,
        Page: 1,
        Name: convertToEnglishNumber(customerDataSearch?.Name || ""),
      };
      await dispatch(getAllMoshtariTblData(params));
      setCurrentPage(1);
    } catch (err) {
      console.error("[catch error]", err);
    }
    setLoading(false);
  };

  const setValue = (name, value) => {
    dispatch(changeCustomerSearch({ Name: value }));
  };

  const onSearch = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const params = {
        KeyGroup: read("groupKey"),
        KeyUser: read("userKey"),
        TypeRegister: controls.customersType.value.value,
        Limit: sizePerPage,
        Page: 1,
        Name: convertToEnglishNumber(customerDataSearch.Name),
      };
      await dispatch(getAllMoshtariTblData(params));
      setCurrentPage(1);
    } catch (err) {
      console.error("[catch error]", err);
    }
    setLoading(false);
  };

  const onClearSearch = async (e) => {
    e.preventDefault();
    dispatch(changeCustomerSearch({ Name: "" }));
    try {
      setLoading(true);
      const params = {
        KeyGroup: read("groupKey"),
        KeyUser: read("userKey"),
        TypeRegister: controls.customersType.value.value,
        Limit: sizePerPage,
        Page: 1,
      };
      await dispatch(getAllMoshtariTblData(params));
      setCurrentPage(1);
    } catch (err) {
      console.error("[catch error]", err);
    }
    setLoading(false);
  };

  const onTableChange = async (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      try {
        setLoading(true);
        const params = {
          KeyGroup: read("groupKey"),
          KeyUser: read("userKey"),
          TypeRegister: controls.customersType.value.value,
          Limit: sizePerPage,
          Page: page,
          Name: customerDataSearch?.Name,
        };
        await dispatch(getAllMoshtariTblData(params));
        setCurrentPage(page);
        setSizePerPage(sizePerPage);
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    }
  };

  return (
    <Fragment>
      <BackDropLoading loading={loading} />
      <Grid container spacing={2} className="mb-2">
        {Object.keys(controls).map((key, index) => {
          return (
            <Grid item xs={12} sm={6} md={3} lg={2} key={key + index}>
              <MuiAutoComplete
                options={controls[key].selectItems}
                label={<Translator id={key} />}
                value={controls[key].value}
                id={key}
                onChange={autoCompleteHandler}
              />
            </Grid>
          );
        })}
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          lg={3}
          className="d-flex flex-row align-items-end"
        >
          <Input
            setValue={setValue.bind(this)}
            name="search"
            value={customerDataSearch ? customerDataSearch?.Name : ""}
            placeholder={t("nameSearch")}
            type="search"
            onSearch={onSearch}
            onClearSearch={onClearSearch}
          />
        </Grid>
      </Grid>
      <RemoteTable
        data={tblData}
        columns={customerColumn}
        loading={loading}
        page={currentPage}
        sizePerPage={sizePerPage}
        totalSize={total}
        onTableChange={onTableChange}
        title={<Translator id="list_moshtari" />}
      />
    </Fragment>
  );
}

export default CustomerTable;
